import { Component } from "@theme/component";

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  async connectedCallback() {
    super.connectedCallback();
    /** @type {HTMLElement[]} */
    const deliveryNoticeSlides = Array.from(
      this.querySelectorAll(".announcement-bar__delivery-notice"),
    );
    //If the annoucement bar contains any delivery notices hide or display them before the slideshow is setup
    if (deliveryNoticeSlides.length > 0) {
      await this.getBankHolidays();
      if (this.bankHolidays) {
        this.updateDeliveryNoticeSlides(deliveryNoticeSlides);
      }
    }

    this.addEventListener("mouseenter", this.suspend);
    this.addEventListener("mouseleave", this.resume);
    document.addEventListener("visibilitychange", this.#handleVisibilityChange);

    this.play();

    //Update the delivery notice slides every minute
    if (!this.bankHolidays) return;
    const interval = 6000; // 60,000ms = 1 minute
    setInterval(() => {
      /** @type {HTMLElement[]} */
      const latestDeliveryNoticeSlides = Array.from(
        this.querySelectorAll(".announcement-bar__delivery-notice"),
      );
      this.updateDeliveryNoticeSlides(latestDeliveryNoticeSlides);
    }, interval);
  }

  async getBankHolidays() {
    try {
      //Obtain bank holidays using https://www.api.gov.uk/gds/bank-holidays/
      const response = await fetch("https://www.gov.uk/bank-holidays.json");
      const result = await response.json();
      const englishBankHolidayArr = result["england-and-wales"]?.events;
      if (!englishBankHolidayArr)
        throw new Error(
          "Can't find English bank holidays, check API documentation",
        );
      this.bankHolidays = englishBankHolidayArr;
    } catch (error) {
      console.error("Bank holidays API error:", error.message);
      /** @type {HTMLElement[]} */
      const deliveryNoticeSlides = Array.from(
        this.querySelectorAll(".announcement-bar__delivery-notice"),
      );
      this.updateAnnouncementBarUi(deliveryNoticeSlides);
    }
  }
  /**
   * @param {HTMLElement[]} slides
   */
  updateDeliveryNoticeSlides(slides) {
    //Check if today or tomorrow is a weekend or bank holiday - Will use this when updating the slides
    const now = new Date();
    const tomorrow = new Date(now.setDate(now.getDate() + 1));

    const isTodayWeekend = now.getDay() == 7 || now.getDay() == 0;
    const isTomorrowWeekend = tomorrow.getDay() == 7 || tomorrow.getDay() == 0;

    const todayMonthNo = now.getMonth() + 1;
    const todayMonth = todayMonthNo < 10 ? "0" + todayMonthNo : todayMonthNo;
    const todayDay = now.getDate() < 10 ? "0" + now.getDate() : now.getDate();
    const formattedToday =
      now.getFullYear() + "-" + todayMonth + "-" + todayDay;

    const tomorrowMonthNo = tomorrow.getMonth() + 1;
    const tomorrowMonth =
      tomorrowMonthNo < 10 ? "0" + tomorrowMonthNo : tomorrowMonthNo;
    const tomorrowDay =
      tomorrow.getDate() < 10 ? "0" + tomorrow.getDate() : tomorrow.getDate();
    const formattedTomorrow =
      tomorrow.getFullYear() + "-" + tomorrowMonth + "-" + tomorrowDay;

    this.isTodayBankHoliday = this.bankHolidays.some(
      (/** @type {{ date: string; }} */ bankHoliday) =>
        bankHoliday.date == formattedToday,
    );
    this.isTomorrowBankHoliday = this.bankHolidays.some(
      (/** @type {{ date: string; }} */ bankHoliday) =>
        bankHoliday.date == formattedTomorrow,
    );

    //Don't show a delivery notice today/tomorrow if it is a bank holiday or weekend that day
    this.showTodayDeliveryNotice = !isTodayWeekend && !this.isTodayBankHoliday;
    this.showTomorrowDeliveryNotice =
      !isTomorrowWeekend && !this.isTomorrowBankHoliday;

    slides.forEach((slide) => this.updateDeliveryNoticeSlide(slide));
  }

  /**
   * @param {HTMLElement} slide
   */
  updateDeliveryNoticeSlide(slide) {
    //14:00 or 11:15 etc in UK time
    const cutoffTime = slide.dataset.cutoffTime;

    if (!cutoffTime) {
      console.error("No cutoff time set for delivery notice");
      return;
    }

    const [cutoffHours, cutoffMins] = cutoffTime.split(":").map(Number);
    if (cutoffHours == undefined || cutoffMins == undefined) {
      console.error("Cutoff time incorrectly set for delivery notice");
      return;
    }
    const now = new Date();

    // Get current time in UK timezone (handles DST automatically)
    const ukFormatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    });

    const ukParts = ukFormatter.formatToParts(now);
    // @ts-ignore
    const ukHour = parseInt(ukParts.find((p) => p.type === "hour").value);
    // @ts-ignore
    const ukMinute = parseInt(ukParts.find((p) => p.type === "minute").value);

    // Total seconds elapsed in the UK day so far
    const ukSecondsElapsed = ukHour * 3600 + ukMinute * 60;

    const secondsElapsedTillCutoff = cutoffHours * 3600 + cutoffMins * 600;

    let ukSecondsTillCutoff = secondsElapsedTillCutoff - ukSecondsElapsed;

    let isCutoffTomorrow = false;
    if (ukSecondsTillCutoff <= 0) {
      isCutoffTomorrow = true;
      ukSecondsTillCutoff += 24 * 3600;
    }

    //If the cutoff is today and today's delivery notice shouldn't show, remove the delivery notice - Same for tomorrow
    if (
      (!isCutoffTomorrow && !this.showTodayDeliveryNotice) ||
      (isCutoffTomorrow && !this.showTomorrowDeliveryNotice)
    ) {
      this.updateAnnouncementBarUi([slide]);
      return;
    }

    this.updateDeliveryNoticeText(slide, ukSecondsTillCutoff, isCutoffTomorrow);

    //Show the delivery notice now its text is correct
    slide.classList.remove("announcement-bar__slide--hidden");
  }

  /**
   * @description Updates single delivery notice text using its cutoff time
   * @param {HTMLElement} slide
   * @param {number} ukSecondsTillCutoff
   * @param {boolean} isCutoffTomorrow
   */
  updateDeliveryNoticeText(slide, ukSecondsTillCutoff, isCutoffTomorrow) {
    /** @type {HTMLElement | null} */
    const deliveryNoticeText = slide.querySelector("delivery-notice-text");
    if (!deliveryNoticeText) return;
    const templateText = deliveryNoticeText?.dataset?.cutoffText;
    if (!templateText) return;

    const now = new Date();

    const diffHours = Math.floor(ukSecondsTillCutoff / 3600);
    let diffMins = Math.floor((ukSecondsTillCutoff % 3600) / 60);

    const hourDiffStr =
      diffHours !== 0 ? `${diffHours}hr${diffHours !== 1 ? "s" : ""}` : "";
    const minDiffStr =
      diffMins !== 0 ? `${diffMins}min${diffMins !== 1 ? "s" : ""}` : "";
    const timeStr = `${hourDiffStr}${hourDiffStr && minDiffStr ? " " : ""}${minDiffStr}`;

    let dayStr = isCutoffTomorrow ? "tomorrow" : "today";

    deliveryNoticeText.textContent = templateText
      .replace("[[day]]", dayStr)
      .replace("[[time]]", timeStr);
  }
  /**
   * @param {HTMLElement[]} [slidesToRemove]
   */
  updateAnnouncementBarUi(slidesToRemove) {
    if (slidesToRemove) {
      slidesToRemove.forEach((slide) => {
        slide.remove();
        this.refs.slides = this.refs.slides?.filter((s) => s != slide);
      });
    }
    //Delete the section if there are no annoucements left to show
    const slideshowSlides = Array.from(
      this.querySelectorAll("slideshow-slide"),
    );
    const slideshowArrows = this.querySelector("slideshow-arrows");
    //Delete the section if there are no annoucements left to show
    /**@todo To avoid the arrows flashing on load, instead hide the slideshow arrows by default, then show if everything is okay */
    if (slideshowSlides.length == 0) this.remove();
    if (slideshowSlides.length == 1 && slideshowArrows)
      slideshowArrows.remove();

    this.current = 0;
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(":hover") || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute("paused");
  }

  set paused(paused) {
    this.toggleAttribute("paused", paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute("autoplay");
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute("aria-hidden", `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () =>
    document.hidden ? this.pause() : this.resume();
}

if (!customElements.get("announcement-bar-component")) {
  customElements.define("announcement-bar-component", AnnouncementBar);
}
